/**
 * Phase 27.46 Phase 2 — Retail Program detail dialog.
 *
 * Two modes stacked in one view:
 *   1. Request header + workflow actions (KR-only for Sent-to-Carrier
 *      and Complete; requester or Agency admin for Submit-for-Review).
 *   2. Inline slots table where KR records the carrier response per
 *      row without a full reload — updates land back into local state.
 *
 * The original requested date/time is always shown next to the
 * approved/modified values so KR can compare at a glance.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, XCircle, Send, MailCheck, ClipboardList,
  MapPin, User, Store, Pencil, Save, X, Trash2, TrendingUp, ArrowRight,
} from 'lucide-react';
import { retailProgramAPI, deletionAuditAPI } from '@/lib/api';
import DeleteRequestDialog from '@/components/marketing/DeleteRequestDialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TimeSelect, to12h } from '@/components/common/TimeSelect';

const CARRIER_META = {
  devoted:  { label: 'Devoted',  tone: 'bg-blue-100 text-blue-800' },
  solis:    { label: 'Solis',    tone: 'bg-emerald-100 text-emerald-800' },
  elevance: { label: 'Elevance', tone: 'bg-indigo-100 text-indigo-800' },
};

const PROGRAM_LABELS = {
  cvs: 'CVS Retail', table_top: 'Table Top', marketing_site: 'Marketing Site',
};
const SPONSOR_LABELS = {
  kr: 'KR (direct)', devoted: 'Devoted', solis: 'Solis', elevance: 'Elevance',
};

const STATUS_META = {
  draft:              { label: 'Draft',              tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  submitted:          { label: 'Submitted',          tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  kr_review:          { label: 'KR Review',          tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  sent_to_carrier:    { label: 'Sent to Carrier',    tone: 'bg-amber-100 text-amber-800 border-amber-300' },
  carrier_responded:  { label: 'Carrier Responded',  tone: 'bg-blue-100 text-blue-800 border-blue-200' },
  partially_approved: { label: 'Partially Approved', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  approved:           { label: 'Approved',           tone: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  declined:           { label: 'Declined',           tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  completed:          { label: 'Completed',          tone: 'bg-slate-200 text-slate-700 border-slate-300' },
};

const SLOT_TONE = {
  requested: 'bg-slate-100 text-slate-700 border-slate-200',
  approved:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  modified:  'bg-amber-100 text-amber-800 border-amber-300',
  declined:  'bg-rose-100 text-rose-800 border-rose-300',
};

const DECLINE_REASONS = [
  { value: 'store_unavailable', label: 'Store Not Available' },
  { value: 'slot_unavailable',  label: 'Slot / Time Not Available' },
  { value: 'agent_ineligible',  label: 'Agent Not Eligible' },
  { value: 'carrier_denied',    label: 'Carrier Denied' },
  { value: 'other',             label: 'Other' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function RetailRequestDetailDialog({ requestId, isKR, onOpenChange, onChanged }) {
  const open = !!requestId;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [metrics, setMetrics] = useState(null);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const r = await retailProgramAPI.get(requestId);
      setData(r.data);
      // Phase 27.53 — Marketing ROI metrics (best-effort, non-blocking).
      retailProgramAPI.leadMetrics(requestId)
        .then((mr) => setMetrics(mr.data))
        .catch(() => setMetrics(null));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load request');
      onOpenChange?.(false);
    } finally {
      setLoading(false);
    }
  }, [requestId, onOpenChange]);

  useEffect(() => {
    if (open) {
      setEditingSlot(null);
      load();
    } else {
      setData(null);
    }
  }, [open, load]);

  // Runs a workflow action + refreshes local state without closing.
  const runAction = async (fn, successMsg) => {
    setSavingAction(true);
    try {
      const r = await fn();
      toast.success(successMsg);
      setData(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setSavingAction(false);
    }
  };

  const status = data?.status;
  const krSponsored = !!data && !data.carrier;
  // Only KR handles carrier-side transitions.
  const showSubmitKr = !['kr_review', 'sent_to_carrier', 'carrier_responded',
                          'partially_approved', 'approved', 'declined',
                          'completed'].includes(status);
  // Carrier-sponsored requests get the "Sent to Carrier" step; KR-sponsored
  // ones are approved directly from KR review (no carrier hop).
  const showMarkSent = isKR && !krSponsored && ['submitted', 'kr_review'].includes(status);
  const showComplete = isKR && ['approved', 'partially_approved', 'declined'].includes(status);
  const canRecordResponses = isKR && (
    ['sent_to_carrier', 'carrier_responded', 'partially_approved', 'approved', 'declined'].includes(status)
    || (krSponsored && ['kr_review', 'submitted'].includes(status))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto"
        data-testid="retail-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
            <Store size={18} className="text-emerald-600" />
            {data ? `${data.location_name || CARRIER_META[data.carrier]?.label || data.carrier || 'Retail'} · ${data.city}, ${data.state}` : 'Retail Request'}
            {status && (
              <Badge
                variant="outline"
                className={`${STATUS_META[status]?.tone || 'bg-slate-100'} text-[11px] font-medium`}
                data-testid="retail-detail-status"
              >
                {STATUS_META[status]?.label || status}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Meta grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MetaRow icon={MapPin} label={krSponsored || (data.program_type && data.program_type !== 'cvs') ? 'Location' : 'Store'}>
                <div className="font-medium text-slate-900">{data.location_name || data.store_address}</div>
                <div className="text-xs text-slate-500">{data.city}, {data.state} {data.zip_code}</div>
                <div className="text-xs text-slate-500">
                  {PROGRAM_LABELS[data.program_type || 'cvs']} · Sponsor: {SPONSOR_LABELS[data.sponsor] || CARRIER_META[data.carrier]?.label || 'KR (direct)'}
                </div>
              </MetaRow>
              <MetaRow icon={User} label="Agent">
                <div className="font-medium text-slate-900">{data.assigned_agent_name || '—'}</div>
                <div className="text-xs text-slate-500">NPN {data.assigned_agent_npn || '—'}</div>
              </MetaRow>
              <MetaRow icon={ClipboardList} label="Submitted">
                <div className="text-slate-800">{fmtDateTime(data.created_at)}</div>
                <div className="text-xs text-slate-500">
                  by {data.requester_name || '—'} · {(data.requester_role || '').replace('_', ' ')}
                </div>
              </MetaRow>
              <MetaRow icon={MailCheck} label={krSponsored ? 'Sponsorship' : 'Sent to carrier'}>
                {krSponsored
                  ? <div className="text-xs text-slate-600">KR-sponsored — approved directly (no carrier step).</div>
                  : (data.sent_to_carrier_at
                    ? <div className="text-slate-800">{fmtDateTime(data.sent_to_carrier_at)}</div>
                    : <div className="text-xs italic text-slate-400">Not yet sent</div>)}
              </MetaRow>
            </div>

            {/* Slots table */}
            <div className="rounded-lg border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Requested slots ({(data.slots || []).length})
                </p>
                {canRecordResponses && (
                  <p className="text-[11px] text-slate-500 italic">
                    Click a row to record the {krSponsored ? 'KR' : 'carrier'} decision.
                  </p>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold">Requested</th>
                      <th className="text-left px-3 py-2 font-semibold">{krSponsored ? 'KR Decision' : 'Carrier Decision'}</th>
                      <th className="text-left px-3 py-2 font-semibold">Approved / Modified</th>
                      <th className="text-left px-3 py-2 font-semibold">Notes</th>
                      {canRecordResponses && <th className="w-8"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.slots || []).map((slot) => (
                      <SlotRow
                        key={slot.slot_id}
                        slot={slot}
                        editing={editingSlot === slot.slot_id}
                        canRecord={canRecordResponses}
                        onEdit={() => setEditingSlot(slot.slot_id)}
                        onCancel={() => setEditingSlot(null)}
                        onSaved={(updated) => {
                          setData(updated);
                          setEditingSlot(null);
                          onChanged?.();
                        }}
                        requestId={data.request_id}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Timeline */}
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <ClipboardList size={13} /> History
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {(data.timeline || []).slice().reverse().map((t, i) => (
                  <div key={i} className="text-xs text-slate-600 flex gap-2">
                    <span className="text-slate-400 font-mono whitespace-nowrap">
                      {fmtDateTime(t.ts)}
                    </span>
                    <span className="font-medium text-slate-800">{t.action}</span>
                    {t.actor_name && <span className="text-slate-500">by {t.actor_name}</span>}
                    {t.note && <span className="text-slate-500 italic">— {t.note}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Leads Captured (ROI panel) ─────────────── */}
            {metrics && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
                data-testid="retail-leads-captured-panel"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-xs uppercase tracking-wider text-emerald-900 font-semibold flex items-center gap-1.5">
                    <TrendingUp size={13} /> Leads Captured
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-emerald-800 hover:bg-emerald-100"
                    onClick={() => {
                      onOpenChange?.(false);
                      navigate(`/leads?retail_program_request_id=${requestId}`);
                    }}
                    disabled={!metrics.total}
                    data-testid="retail-view-leads-btn"
                  >
                    View leads <ArrowRight size={12} className="ml-1" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="retail-metric-total">
                      {metrics.total}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Total</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="retail-metric-closed">
                      {metrics.closed}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Closed Won</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-900" data-testid="retail-metric-rate">
                      {(metrics.conversion_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Conv. Rate</div>
                  </div>
                </div>
                {metrics.total === 0 && (
                  <div className="mt-2 text-[11px] text-emerald-700 italic">
                    No leads captured yet — use the Live Kiosk to start.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!loading && data && (
          <DialogFooter className="flex-wrap gap-2">
            {showSubmitKr && (
              <Button
                variant="outline" size="sm"
                onClick={() => runAction(
                  () => retailProgramAPI.submitKrReview(requestId, null),
                  'Sent to KR Review',
                )}
                disabled={savingAction}
                data-testid="retail-submit-kr"
              >
                Send to KR Review
              </Button>
            )}
            {showMarkSent && (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => runAction(
                  () => retailProgramAPI.markSentToCarrier(requestId, null),
                  'Marked as sent to carrier',
                )}
                disabled={savingAction}
                data-testid="retail-mark-sent"
              >
                <Send size={14} className="mr-1" /> Mark Sent to Carrier
              </Button>
            )}
            {showComplete && (
              <Button
                variant="outline" size="sm"
                onClick={() => runAction(
                  () => retailProgramAPI.complete(requestId),
                  'Request completed',
                )}
                disabled={savingAction}
                data-testid="retail-complete"
              >
                Mark completed
              </Button>
            )}
            {isKR && (
              <Button
                variant="outline" size="sm"
                className="border-rose-300 hover:bg-rose-50 text-rose-700 ml-auto"
                onClick={() => setConfirmDelete(true)}
                disabled={savingAction}
                data-testid="retail-delete"
              >
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
      <DeleteRequestDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        resourceLabel="retail request"
        summary={data ? `${data.retailer || data.carrier} — ${data.city}, ${data.state}` : ''}
        onConfirm={async ({ reason, reasonPreset }) => {
          setSavingAction(true);
          try {
            const r = await retailProgramAPI.delete(requestId, {
              reason, reason_preset: reasonPreset,
            });
            const auditId = r.data?.audit_id;
            toast.success('Request deleted', {
              duration: 10_000,
              action: auditId ? {
                label: 'Undo',
                onClick: async () => {
                  try {
                    await deletionAuditAPI.restore(auditId);
                    toast.success('Request restored');
                    onChanged?.();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Restore failed');
                  }
                },
              } : undefined,
            });
            onChanged?.();
            onOpenChange?.(false);
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Delete failed');
            throw err;
          } finally {
            setSavingAction(false);
          }
        }}
      />
    </Dialog>
  );
}

function MetaRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        {children}
      </div>
    </div>
  );
}

function SlotRow({ slot, editing, canRecord, onEdit, onCancel, onSaved, requestId }) {
  const [decision, setDecision] = useState(slot.carrier_response);
  const [approvedDate, setApprovedDate] = useState(slot.approved_date || slot.requested_date || '');
  const [approvedStart, setApprovedStart] = useState(slot.approved_start || slot.requested_start || '');
  const [approvedEnd, setApprovedEnd] = useState(slot.approved_end || slot.requested_end || '');
  const [declineReason, setDeclineReason] = useState(slot.decline_reason || 'store_unavailable');
  const [declineNote, setDeclineNote] = useState(slot.decline_note || '');
  const [carrierNote, setCarrierNote] = useState(slot.carrier_note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setDecision(slot.carrier_response);
      setApprovedDate(slot.approved_date || slot.requested_date || '');
      setApprovedStart(slot.approved_start || slot.requested_start || '');
      setApprovedEnd(slot.approved_end || slot.requested_end || '');
      setDeclineReason(slot.decline_reason || 'store_unavailable');
      setDeclineNote(slot.decline_note || '');
      setCarrierNote(slot.carrier_note || '');
    }
  }, [editing, slot]);

  const save = async () => {
    if (decision === 'declined' && !declineReason) {
      toast.error('Pick a decline reason'); return;
    }
    if (decision === 'declined' && declineReason === 'other' && !declineNote.trim()) {
      toast.error('Decline note is required for "Other"'); return;
    }
    if (decision === 'modified' && (!approvedDate || !approvedStart || !approvedEnd)) {
      toast.error('Modified slots need approved date + start + end'); return;
    }
    if ((decision === 'approved' || decision === 'modified') && approvedEnd <= approvedStart) {
      toast.error('End time must be after start time'); return;
    }
    // Preserve scroll position across the async save (per spec §9).
    const scroll = document.querySelector('[data-testid="retail-detail-dialog"]')?.scrollTop;
    setSaving(true);
    try {
      const payload = { carrier_response: decision };
      if (decision === 'approved') {
        // Approved means exact requested — no override fields.
      } else if (decision === 'modified') {
        payload.approved_date = approvedDate;
        payload.approved_start = approvedStart;
        payload.approved_end = approvedEnd;
        if (carrierNote.trim()) payload.carrier_note = carrierNote.trim();
      } else if (decision === 'declined') {
        payload.decline_reason = declineReason;
        if (declineNote.trim()) payload.decline_note = declineNote.trim();
      }
      const r = await retailProgramAPI.recordSlotResponse(requestId, slot.slot_id, payload);
      onSaved(r.data);
      queueMicrotask(() => {
        const el = document.querySelector('[data-testid="retail-detail-dialog"]');
        if (el && scroll != null) el.scrollTop = scroll;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const badgeTone = SLOT_TONE[slot.carrier_response] || SLOT_TONE.requested;

  if (!editing) {
    return (
      <tr
        className={canRecord ? 'hover:bg-slate-50 cursor-pointer' : ''}
        onClick={canRecord ? onEdit : undefined}
        data-testid={`retail-slot-row-${slot.slot_id}`}
      >
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="font-medium text-slate-900">{fmtDate(slot.requested_date)}</div>
          <div className="text-[11px] text-slate-500">{to12h(slot.requested_start)} – {to12h(slot.requested_end)}</div>
        </td>
        <td className="px-3 py-2">
          <Badge variant="outline" className={`${badgeTone} border font-medium text-[10px] capitalize`}>
            {slot.carrier_response || 'requested'}
          </Badge>
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          {slot.carrier_response === 'approved' && (
            <span className="text-emerald-700 text-[11px] italic">Exact requested</span>
          )}
          {slot.carrier_response === 'modified' && (
            <div>
              <div className="text-slate-900 text-sm">{fmtDate(slot.approved_date)}</div>
              <div className="text-[11px] text-slate-500">{to12h(slot.approved_start)} – {to12h(slot.approved_end)}</div>
            </div>
          )}
          {slot.carrier_response === 'declined' && slot.decline_reason && (
            <span className="text-rose-700 text-[11px] capitalize">
              {slot.decline_reason.replace(/_/g, ' ')}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-[11px] text-slate-600 truncate max-w-[200px]">
          {slot.carrier_note || slot.decline_note || '—'}
        </td>
        {canRecord && (
          <td className="px-2 py-2 text-slate-400">
            <Pencil size={12} />
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr className="bg-slate-50" data-testid={`retail-slot-edit-${slot.slot_id}`}>
      <td colSpan={canRecord ? 5 : 4} className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Requested</Label>
            <p className="text-sm font-medium text-slate-900">{fmtDate(slot.requested_date)}</p>
            <p className="text-[11px] text-slate-500">{to12h(slot.requested_start)} – {to12h(slot.requested_end)}</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Carrier decision</Label>
            <Select value={decision} onValueChange={setDecision}>
              <SelectTrigger data-testid={`retail-slot-decision-${slot.slot_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved (exact)</SelectItem>
                <SelectItem value="modified">Modified</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="requested">Reset (no response)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {decision === 'modified' && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Approved date</Label>
                <Input
                  type="date" value={approvedDate}
                  onChange={(e) => setApprovedDate(e.target.value)}
                  data-testid={`retail-slot-approved-date-${slot.slot_id}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Start</Label>
                  <TimeSelect value={approvedStart} onChange={setApprovedStart}
                    testId={`retail-slot-approved-start-${slot.slot_id}`} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">End</Label>
                  <TimeSelect value={approvedEnd} onChange={setApprovedEnd}
                    testId={`retail-slot-approved-end-${slot.slot_id}`} />
                </div>
              </div>
            </>
          )}

          {decision === 'declined' && (
            <>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Reason</Label>
                <Select value={declineReason} onValueChange={setDeclineReason}>
                  <SelectTrigger data-testid={`retail-slot-decline-reason-${slot.slot_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DECLINE_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Note {declineReason === 'other' ? '*' : ''}
                </Label>
                <Input
                  value={declineNote}
                  onChange={(e) => setDeclineNote(e.target.value)}
                  placeholder="Optional details"
                  data-testid={`retail-slot-decline-note-${slot.slot_id}`}
                />
              </div>
            </>
          )}

          {(decision === 'modified' || decision === 'approved') && (
            <div className="md:col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Carrier note (optional)</Label>
              <Textarea
                rows={2}
                value={carrierNote}
                onChange={(e) => setCarrierNote(e.target.value)}
                placeholder="Additional context from the carrier"
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            <X size={13} className="mr-1" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            data-testid={`retail-slot-save-${slot.slot_id}`}
          >
            {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />}
            Save
          </Button>
        </div>
      </td>
    </tr>
  );
}
